import { defineStore } from 'pinia';

import { useConstructionMonitoring } from '@/composables/constructionMonitoring';
import { useDict } from '@/composables/fischerPlugin';
import type {
  ConstructionMonitoringArea,
  ConstructionRoom,
  ConstructionSite,
  SensorBolt,
  SensorDisc,
  SensorMetadata,
} from '@/generated/graphql';

export interface ChartsFilter {
  filterAll: boolean;
  showForce: boolean;
  showLoad: boolean;
  showTemperature: boolean;
  showLimits: boolean;
  showPoint: boolean;
  currentRange: number;
  zoomMin: number | null;
  zoomMax: number | null;
  scaleYMin: number | null;
  scaleYMax: number | null;
}

export const useConstructionMonitoringStore = defineStore('construction-monitoring', {
  state() {
    return {
      pageType: null as string | null,
      projectId: null as string | null,
      project: null as ConstructionSite | null,
      templateVersion: '3' as string,
      areaId: null as string | null,
      area: null as ConstructionMonitoringArea | null,
      roomId: null as string | null,
      room: null as ConstructionRoom | null,
      sensorId: null as string | null,
      sensorType: null as string | null,
      sensorTypeApi: null as string | null,
      sensorTypeApiVersion: null as string | null,
      sensorAddMode: null as string | null,
      sensor: null as SensorBolt | SensorDisc | null,
      sensorMetadata: null as SensorMetadata | null,
      sensorBoltSummary: null as SensorBolt[] | null,
      sensorDiscSummary: null as SensorDisc[] | null,
      isLoading: false as boolean,
      charts: {
        filterAll: true,
        showForce: true,
        showLoad: true,
        showTemperature: true,
        showLimits: true,
        showPoint: false,
        currentRange: 30,
        zoomMin: null,
        zoomMax: null,
        scaleYMin: null,
        scaleYMax: null,
      } as ChartsFilter,
    };
  },
  actions: {
    async setPageType(pageType: string, isDeepLink?: boolean) {
      const { loadConstructionMonitoringData, setBrowserPageTitle, getCMUrl } =
        useConstructionMonitoring();
      const dict = useDict();

      this.pageType = pageType;

      if (this.projectId) {
        await loadConstructionMonitoringData();
      }

      if (isDeepLink) {
        let deepLink = null;
        if (this.sensor) {
          deepLink = getCMUrl(this.sensor?.name, this.sensorId, this.sensorType);
        } else if (this.room) {
          deepLink = getCMUrl(this.room?.name, this.roomId, 'room');
        } else if (this.area) {
          deepLink = getCMUrl(this.area?.name, this.areaId, 'area');
        } else if (this.project) {
          deepLink = getCMUrl(this.project?.name, this.projectId, 'project');
        }
        window.history.replaceState(null, '', deepLink);
      }

      let browserTitle = this.room?.name || this.area?.name || this.project?.name || '';

      if (pageType === 'manage-participants') {
        browserTitle =
          dict.get('construction-Projects.Breadcrumb.Manage-Participants') || 'Manage Participants';
      }

      if (pageType === 'search') {
        browserTitle = dict.get('construction-Projects.Breadcrumb.Search') || 'Search';
      }

      setBrowserPageTitle(browserTitle);
    },
    setSensorType(sensorType: string) {
      this.sensorType = sensorType;
      if (sensorType === 'sensor-bolt') {
        this.sensorTypeApi = 'bolts';
        this.sensorTypeApiVersion = 'v1';
      }
      if (sensorType === 'sensor-disc') {
        this.sensorTypeApi = 'discs';
        this.sensorTypeApiVersion = 'v2';
      }
    },
  },
  getters: {
    isOverviewPage(state) {
      return state.pageType === 'overview';
    },
    isProjectPage(state) {
      return state.pageType === 'project';
    },
    isProjectAreaPage(state) {
      return state.pageType === 'area';
    },
    isProjectRoomPage(state) {
      return state.pageType === 'room';
    },
    isSensorPage(state) {
      return state.pageType === 'sensor';
    },
    isManageParticipantsPage(state) {
      return state.pageType === 'manage-participants';
    },
    isSearchPage(state) {
      return state.pageType === 'search';
    },
    isAdmin(state) {
      return state.project?.role === 'ADMIN';
    },
    isOffice(state) {
      return state.project?.role === 'OFFICE';
    },
    isAuditor(state) {
      return state.project?.role === 'AUDITOR';
    },
    isEmployee(state) {
      return state.project?.role === 'EMPLOYEE';
    },
    isOwner(state) {
      return state.project?.role === 'OWNER';
    },
    isContributor(state) {
      return state.project?.role === 'CONTRIBUTOR';
    },
    isViewer(state) {
      return state.project?.role === 'VIEWER';
    },
    isDemo(state) {
      return state.project?.role === 'DEMO';
    },
  },
});
